import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Radio,
  ArrowLeft,
  Search,
  Plus,
  Download,
  Upload,
  X,
  Edit2,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Clock,
  Signal,
  WifiOff,
  User,
  Copy,
  MoreVertical
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { useSeries } from '../../App';

// Transponder statuses
const transponderStatuses = {
  active: { label: 'Active', color: 'text-emerald-600 bg-emerald-50 border-emerald-200', icon: CheckCircle2 },
  inactive: { label: 'Inactive', color: 'text-slate-600 bg-slate-50 border-slate-200', icon: WifiOff },
  issue: { label: 'Issue', color: 'text-red-600 bg-red-50 border-red-200', icon: AlertCircle },
  loaner: { label: 'Loaner', color: 'text-amber-600 bg-amber-50 border-amber-200', icon: Clock },
};

// Mock transponder data
const mockTransponders = [
  {
    id: 1,
    transponder_number: '1234567',
    car_number: '1A',
    driver_name: 'Brad Sweet',
    team: 'Kasey Kahne Racing',
    status: 'active',
    type: 'X2',
    last_seen: '2025-12-10T14:30:00',
    assigned_date: '2025-01-15',
    notes: '',
  },
  {
    id: 2,
    transponder_number: '2345678',
    car_number: '49',
    driver_name: 'Donny Schatz',
    team: 'Tony Stewart Racing',
    status: 'active',
    type: 'X2',
    last_seen: '2025-12-10T14:28:00',
    assigned_date: '2025-01-15',
    notes: '',
  },
  {
    id: 3,
    transponder_number: '3456789',
    car_number: '2',
    driver_name: 'David Gravel',
    team: 'Big Game Motorsports',
    status: 'active',
    type: 'X2',
    last_seen: '2025-12-10T14:25:00',
    assigned_date: '2025-02-01',
    notes: '',
  },
  {
    id: 4,
    transponder_number: '4567890',
    car_number: '17',
    driver_name: 'Sheldon Haudenschild',
    team: 'Stenhouse Jr-Marshall Racing',
    status: 'issue',
    type: 'X2',
    last_seen: '2025-12-09T22:15:00',
    assigned_date: '2025-01-20',
    notes: 'Weak signal reported at last event',
  },
  {
    id: 5,
    transponder_number: '5678901',
    car_number: '41',
    driver_name: 'Carson Macedo',
    team: 'Jason Johnson Racing',
    status: 'active',
    type: 'X2',
    last_seen: '2025-12-10T14:22:00',
    assigned_date: '2025-03-01',
    notes: '',
  },
  {
    id: 6,
    transponder_number: '6789012',
    car_number: '83',
    driver_name: 'James McFadden',
    team: 'Roth Motorsports',
    status: 'loaner',
    type: 'X2',
    last_seen: '2025-12-10T14:20:00',
    assigned_date: '2025-12-10',
    notes: 'Loaner for Knoxville - return after event',
  },
  {
    id: 7,
    transponder_number: '7890123',
    car_number: '5',
    driver_name: 'Spencer Bayston',
    team: 'CJB Motorsports',
    status: 'active',
    type: 'X2',
    last_seen: '2025-12-10T14:18:00',
    assigned_date: '2025-01-15',
    notes: '',
  },
  {
    id: 8,
    transponder_number: '8901234',
    car_number: '18',
    driver_name: 'Giovanni Scelzi',
    team: 'KCP Racing',
    status: 'inactive',
    type: 'X2',
    last_seen: '2025-11-15T21:30:00',
    assigned_date: '2025-04-01',
    notes: 'Not competing at current event',
  },
  {
    id: 9,
    transponder_number: 'LOANER-001',
    car_number: null,
    driver_name: null,
    team: null,
    status: 'inactive',
    type: 'X2',
    last_seen: null,
    assigned_date: null,
    notes: 'Series loaner transponder #1',
  },
  {
    id: 10,
    transponder_number: 'LOANER-002',
    car_number: null,
    driver_name: null,
    team: null,
    status: 'inactive',
    type: 'X2',
    last_seen: null,
    assigned_date: null,
    notes: 'Series loaner transponder #2',
  },
];

// Status Badge Component
function StatusBadge({ status }) {
  const config = transponderStatuses[status] || transponderStatuses.inactive;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

// Time Ago Helper
function timeAgo(dateString) {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

// Transponder Row Component
function TransponderRow({ transponder, onEdit, onDelete }) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            transponder.status === 'active' ? 'bg-emerald-500' :
            transponder.status === 'issue' ? 'bg-red-500 animate-pulse' :
            transponder.status === 'loaner' ? 'bg-amber-500' :
            'bg-slate-300'
          }`} />
          <span className="font-mono font-semibold text-slate-800">{transponder.transponder_number}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        {transponder.car_number ? (
          <div className="flex items-center gap-2">
            <div className="w-10 h-8 bg-slate-800 rounded flex items-center justify-center">
              <span className="font-mono font-bold text-white text-sm">{transponder.car_number}</span>
            </div>
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        {transponder.driver_name ? (
          <div>
            <div className="font-medium text-slate-800">{transponder.driver_name}</div>
            <div className="text-xs text-slate-500">{transponder.team}</div>
          </div>
        ) : (
          <span className="text-slate-400 text-sm">Unassigned</span>
        )}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={transponder.status} />
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-slate-500">{transponder.type}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          {transponder.last_seen && transponder.status === 'active' && (
            <Signal className="w-3 h-3 text-emerald-500" />
          )}
          <span className={`text-sm ${
            transponder.last_seen && transponder.status === 'active' ? 'text-slate-600' : 'text-slate-400'
          }`}>
            {timeAgo(transponder.last_seen)}
          </span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-8 z-20 w-40 bg-white rounded-lg shadow-lg border border-slate-200 py-1">
                <button
                  onClick={() => { onEdit(transponder); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => { navigator.clipboard.writeText(transponder.transponder_number); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Copy className="w-4 h-4" />
                  Copy Number
                </button>
                {transponder.driver_name && (
                  <button
                    onClick={() => { onEdit({ ...transponder, car_number: null, driver_name: null, team: null }); setShowMenu(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <X className="w-4 h-4" />
                    Unassign
                  </button>
                )}
                <hr className="my-1 border-slate-100" />
                <button
                  onClick={() => { onDelete(transponder); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

// Edit/Add Modal
function TransponderModal({ isOpen, onClose, transponder, onSave, drivers }) {
  const [formData, setFormData] = useState(() => {
    if (transponder) {
      return {
        transponder_number: transponder.transponder_number || '',
        car_number: transponder.car_number || '',
        driver_name: transponder.driver_name || '',
        team: transponder.team || '',
        status: transponder.status || 'active',
        type: transponder.type || 'X2',
        notes: transponder.notes || '',
      };
    }
    return {
      transponder_number: '',
      car_number: '',
      driver_name: '',
      team: '',
      status: 'active',
      type: 'X2',
      notes: '',
    };
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...transponder, ...formData });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {transponder ? 'Edit Transponder' : 'Add Transponder'}
          </h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Transponder Number</label>
            <Input
              value={formData.transponder_number}
              onChange={(e) => setFormData({ ...formData, transponder_number: e.target.value })}
              placeholder="e.g., 1234567"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Car Number</label>
              <Input
                value={formData.car_number}
                onChange={(e) => setFormData({ ...formData, car_number: e.target.value })}
                placeholder="e.g., 49"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <option value="X2">X2</option>
                <option value="TranX">TranX</option>
                <option value="Classic">Classic</option>
              </Select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Driver Name</label>
            <Input
              value={formData.driver_name}
              onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
              placeholder="Driver name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Team</label>
            <Input
              value={formData.team}
              onChange={(e) => setFormData({ ...formData, team: e.target.value })}
              placeholder="Team name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <Select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="loaner">Loaner</option>
              <option value="issue">Issue</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optional notes"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {transponder ? 'Save Changes' : 'Add Transponder'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TransponderManagement() {
  useParams();
  const currentSeries = useSeries();
  const seriesSlug = currentSeries?.slug;

  const [transponders, setTransponders] = useState(mockTransponders);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingTransponder, setEditingTransponder] = useState(null);

  // Filter transponders
  const filteredTransponders = transponders.filter(t => {
    const matchesSearch =
      t.transponder_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.car_number && t.car_number.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (t.driver_name && t.driver_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (t.team && t.team.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = filterStatus === 'all' || t.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: transponders.length,
    active: transponders.filter(t => t.status === 'active').length,
    assigned: transponders.filter(t => t.driver_name).length,
    issues: transponders.filter(t => t.status === 'issue').length,
    loaners: transponders.filter(t => t.status === 'loaner').length,
  };

  const handleEdit = (transponder) => {
    setEditingTransponder(transponder);
    setShowModal(true);
  };

  const handleDelete = (transponder) => {
    if (window.confirm(`Delete transponder ${transponder.transponder_number}?`)) {
      setTransponders(prev => prev.filter(t => t.id !== transponder.id));
    }
  };

  const handleSave = (data) => {
    if (editingTransponder) {
      setTransponders(prev => prev.map(t =>
        t.id === editingTransponder.id ? { ...t, ...data } : t
      ));
    } else {
      setTransponders(prev => [...prev, {
        ...data,
        id: Date.now(),
        last_seen: null,
        assigned_date: new Date().toISOString().split('T')[0],
      }]);
    }
    setEditingTransponder(null);
  };

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Link
            to={`/${seriesSlug}/tools`}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </Link>
          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
            <Radio className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 className="page-title">Transponder Management</h1>
            <p className="page-subtitle">Assign and track transponder numbers</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button variant="secondary">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => { setEditingTransponder(null); setShowModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Transponder
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Radio className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
                <div className="text-sm text-slate-500">Total</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Signal className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{stats.active}</div>
                <div className="text-sm text-slate-500">Active</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{stats.assigned}</div>
                <div className="text-sm text-slate-500">Assigned</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{stats.loaners}</div>
                <div className="text-sm text-slate-500">Loaners</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{stats.issues}</div>
                <div className="text-sm text-slate-500">Issues</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by transponder, car, driver, or team..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="w-48">
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="loaner">Loaner</option>
                <option value="issue">Issue</option>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transponder Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Transponder #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Car
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Driver / Team
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Last Seen
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-16">

                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTransponders.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <Radio className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-slate-600 mb-2">No Transponders Found</h3>
                      <p className="text-sm text-slate-500">
                        {searchQuery || filterStatus !== 'all'
                          ? 'Try adjusting your search or filters'
                          : 'Add your first transponder to get started'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredTransponders.map(transponder => (
                    <TransponderRow
                      key={transponder.id}
                      transponder={transponder}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      {filteredTransponders.length > 0 && (
        <div className="mt-4 text-sm text-slate-500 text-center">
          Showing {filteredTransponders.length} of {transponders.length} transponders
        </div>
      )}

      {/* Add/Edit Modal */}
      <TransponderModal
        key={editingTransponder?.id ?? 'new'}
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingTransponder(null); }}
        transponder={editingTransponder}
        onSave={handleSave}
      />
    </div>
  );
}
